import { UUID } from "aws-sdk/clients/cloudtrail";
import {
    addMilliseconds,
    addMonths,
    isBefore,
    subMilliseconds,
    subMonths,
} from "date-fns";
import { action, computed, observable } from "mobx";

import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { startOfUTCMinute } from "@formant/date-fns/startOfUTCMinute";
import { startOfUTCMonth } from "@formant/date-fns/startOfUTCMonth";
import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { IEventStore } from "@formant/core/model/data/IEventStore";
import { duration } from "@formant/core/model/duration";
import { EventType } from "@formant/core/model/EventType";
import { IAggregateEvent } from "@formant/core/model/IAggregateEvent";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IComment } from "@formant/core/model/IComment";
import { IEvent } from "@formant/core/model/IEvent";
import { IFilter } from "@formant/core/model/IFilter";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IQuery } from "@formant/core/model/IQuery";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { Uuid } from "@formant/core/model/Uuid";
import { StreamSource } from "@formant/rectangle-sdk/src/types/StreamSource";

import { track } from "../analytics";
import { IModuleConfigurationProps } from "../component/Observe/IModuleConfigurationProps";
import { Mode } from "../model/Mode";
import { clamp } from "../utils/clamp";
import { formatDateString } from "../utils/formatDateString";
import { hashObservable } from "../utils/hashObservable";
import { localSettingObservable } from "../utils/localSettingObservable";
import { IAppStore } from "./IAppStore";
import { IAuthStore } from "./IAuthStore";
import { IObserveStore } from "./IObserveStore";
import { IOrganizationStore } from "./IOrganizationStore";
import { IRouterStore } from "./IRouterStore";
import {
    eventTypeToSeekOption,
    SeekOption,
    seekOptionToEventTypes,
} from "./SeekOption";

type ModuleComponentType = string | StreamSource;

export class ObserveStore implements IObserveStore {
    @hashObservable({
        transformer: {
            to: (_: Date) => _.getTime(),
            from: (_: number) => new Date(_),
        },
        ignore: (_, target) => target.mode !== "pause",
    })
    time: Date = new Date();
    @observable
    humanTime: string = formatDateString(this.time, "HH:mm:ss");
    @observable
    range: [Date, Date] = [new Date(), new Date()];
    @hashObservable({ ignore: mode => mode !== "pause" })
    mode: Mode = "live";
    @hashObservable()
    speed: number = 1;
    @hashObservable()
    eventSidebar: "open" | "close" = "close";
    @hashObservable()
    selectedEventId?: Uuid;
    @observable
    selectedAggregateEvent?: IAggregateEvent;
    @hashObservable()
    fullscreen: boolean = false;
    @hashObservable()
    focusedStream?: [string, AddableModuleType?];
    @observable
    autoScrollToModule?: string;
    @observable
    configuringModule?: [
        string,
        IObserveLayout,
        (layout: IObserveLayout) => void,
    ];
    @observable
    showFleetSettings: boolean = false;
    @observable
    isCustomizing: boolean = false;
    @observable
    configuringFlyout?: [
        IObserveLayout,
        IModuleConfigurationProps,
        (layout: IObserveLayout) => void,
    ];
    @observable
    configuringAnalyticsId?: string;
    @observable
    temporarilyConfiguringModule?: [string];
    @hashObservable()
    hideInactiveStreams: boolean = false;
    @observable
    arrangingModules: boolean = false;
    @observable
    editingDevice: boolean = false;
    @observable
    exploring: boolean = false;
    @observable
    moduleToAdd?: { url: string; name: string };
    @observable
    quickExploration: UUID | undefined;
    @observable
    addModuleForDevices?: Uuid[];
    @observable
    addModuleComponent?: ModuleComponentType;
    @observable
    skipNextLayout: boolean = false;
    @observable
    showEmbedModal: boolean = false;
    @observable.ref
    editingAnnotation?: IAnnotation;
    @observable.ref
    editingComment?: IComment;
    @observable.ref
    exportTelemetryPopupQuery: IQuery | undefined = undefined;
    @observable.ref
    exportAnalyticsPopupQuery: ISqlQuery | undefined = undefined;
    @observable.ref
    hoverEvent: IEvent | undefined = undefined;
    @observable.ref
    hoverAggregateEvent?: IAggregateEvent;
    lastTimeSetAt?: number;
    @localSettingObservable("seek-option")
    seekOptionValue: SeekOption = "Data presence";
    @localSettingObservable("analytics-start-date")
    analyticsTempStartDate?: string = undefined;
    @localSettingObservable("analytics-end-date")
    analyticsTempEndDate?: string = undefined;

    private readonly appStore: IAppStore;
    private readonly authStore: IAuthStore;
    private readonly eventStore: IEventStore;
    private readonly routerStore: IRouterStore;
    private readonly organizationStore: IOrganizationStore;

    constructor({
        appStore,
        authStore,
        eventStore,
        routerStore,
        organizationStore,
    }: {
        appStore: IAppStore;
        authStore: IAuthStore;
        eventStore: IEventStore;
        routerStore: IRouterStore;
        organizationStore: IOrganizationStore;
    }) {
        this.appStore = appStore;
        this.authStore = authStore;
        this.eventStore = eventStore;
        this.routerStore = routerStore;
        this.organizationStore = organizationStore;
    }

    get focusedStreamName(): string | undefined {
        return this.focusedStream?.[0];
    }

    get focusedStreamType(): AddableModuleType | undefined {
        return this.focusedStream?.[1];
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() === b.getTime() })
    get month(): Date {
        return startOfUTCMonth(this.time);
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() === b.getTime() })
    get minute(): Date {
        return startOfUTCMinute(this.time);
    }

    @computed
    get metadataStart(): Date {
        return subMonths(this.month, 1);
    }

    @computed
    get metadataEnd(): Date {
        return addMonths(this.month, 2);
    }

    @computed
    get timescaleMetadataStart(): Date {
        const { timelineScale } = this.appStore;
        return subMilliseconds(
            this.minute,
            clamp(timelineScale, duration.minute, duration.month),
        );
    }

    @computed
    get timescaleMetadataEnd(): Date {
        const { timelineScale } = this.appStore;
        return addMilliseconds(
            this.minute,
            clamp(timelineScale, duration.minute, duration.month),
        );
    }

    // Selects and opens the event.

    get seekOption() {
        return this.seekOptionValue;
    }

    // Navigate to the Device View or Channel for the event.
    // Selects and opens the event.
    // Used when viewing the event requires navigating to the Device View or Channel

    public set seekOption(value: SeekOption) {
        this.seekOptionValue = value;

        // Unselect the selected event if it does not match the new seek option
        // (because the event is no longer visible)
        const { selectedEventId, seekOption } = this;
        const event =
            (selectedEventId && this.eventStore.get(selectedEventId)) ||
            undefined;
        const eventType = event?.type;
        if (
            eventType &&
            seekOption !== "Data presence" &&
            !seekOptionToEventTypes(seekOption).includes(eventType)
        ) {
            this.selectedEventId = undefined;
        }
    }

    @computed
    get queryRange(): { start: Date; end: Date } {
        const { time } = this;
        const timelineScale = clamp(
            this.appStore.timelineScale,
            0,
            duration.day,
        );
        return {
            start: subMilliseconds(time, timelineScale * 0.75),
            end: addMilliseconds(time, timelineScale * 0.25),
        };
    }

    @action
    pauseAt(time: Date): void {
        this.mode = "pause";
        this.setTime(time);
    }

    toggleMode(): void {
        this.mode = this.mode === "pause" ? "play" : "pause";
        if (this.mode === "pause" && this.lastTimeSetAt !== undefined) {
            this.time = new Date(
                this.time.getTime() +
                    this.speed * (Date.now() - this.lastTimeSetAt),
            );
        } else {
            this.lastTimeSetAt = Date.now();
        }
    }

    setTime(time: Date) {
        this.time = time;
        this.lastTimeSetAt = Date.now();
        const newHumanTime = formatDateString(time, "HH:mm:ss");
        if (this.humanTime !== newHumanTime) {
            this.humanTime = newHumanTime;
        }
    }

    toggleFullscreen(isV2?: boolean) {
        this.fullscreen = !isV2
            ? !!(!this.fullscreen && this.focusedStreamName)
            : !this.fullscreen;

        if (this.fullscreen) {
            this.configuringAnalyticsId = undefined;
            this.isCustomizing = false;
            track("Fullscreen Module");
        } else {
            this.appStore.clearSnackbar();
        }
    }

    exportTelemetry(
        filter: IFilter,
        start: Date = new Date(this.time.getTime() - 30 * duration.second),
        end: Date = new Date(this.time.getTime() + 30 * duration.second),
    ) {
        this.exportTelemetryPopupQuery = {
            ...filter,
            start: start.toISOString(),
            end: end.toISOString(),
        };
    }

    editComment(comment: Partial<IComment> = {}) {
        this.mode = "pause";
        this.editingComment = {
            ...comment,
            type: "comment",
            message: comment.message || "",
            time: comment.time || this.time.toISOString(),
            userId: defined(this.authStore.userId),
            streamName: comment.streamName || this.focusedStreamName,
            streamType: comment.streamType || this.focusedStreamType,
        } as IComment;
    }

    @action
    editAnnotation(annotation: Partial<IAnnotation> = {}) {
        this.mode = "pause";
        this.eventSidebar = "open";
        this.editingAnnotation = {
            ...annotation,
            type: "annotation",
            message: annotation.message || "",
            time: annotation.time || this.time.toISOString(),
            userId: defined(this.authStore.userId),
            annotationTemplateId: annotation.annotationTemplateId || "",
            streamName: annotation.streamName || this.focusedStreamName,
            streamType: annotation.streamType || this.focusedStreamType,
        } as IAnnotation;
    }

    @action
    setShowEmbedModal(showEmbedModal: boolean): void {
        this.showEmbedModal = showEmbedModal;
    }

    // Used when the user is already on the on an Observe page.
    @action
    viewEvent(event: IEvent): void {
        const eventId = event.id;
        this.eventSidebar = "open";
        this.selectedEventId = eventId;
        this.selectedAggregateEvent = undefined;
        this.editingAnnotation = undefined;
        this.time = parseIsoDate(event.time);
        this.lastTimeSetAt = Date.now();
        this.mode = "pause";

        if (
            !event.viewed &&
            this.authStore.hasRequiredAccess("events", "operator")
        ) {
            fork(this.eventStore.view({ id: eventId }));
            track("View Event");
        }

        this.fixSeekOption(event.type);
    }

    // (because the user is not currently on that page).
    async navigateToEvent(event: IEvent, channelId?: Uuid): Promise<void> {
        const eventId = defined(event.id);
        const { routeTo, navigate } = this.routerStore;

        const hashParameters = {
            eventSidebar: "open",
            selectedEventId: eventId,
            time: parseIsoDate(event.time),
            mode: "pause",
        };

        const route = channelId
            ? routeTo.channel({ id: channelId }, hashParameters)
            : routeTo.device({ id: defined(event.deviceId) }, hashParameters);

        await navigate(route, { replace: true });

        this.selectedAggregateEvent = undefined;
        this.editingAnnotation = undefined;

        if (
            !event.viewed &&
            this.authStore.hasRequiredAccess("events", "operator")
        ) {
            fork(this.eventStore.view({ id: eventId }));
            track("View Event");
        }

        this.fixSeekOption(event.type);
    }

    @action
    closeEventSidebar(): void {
        this.eventSidebar = "close";
        this.selectedEventId = undefined;
        this.selectedAggregateEvent = undefined;
        this.editingAnnotation = undefined;
    }

    @action
    showEventList(aggregate?: IAggregateEvent): void {
        this.eventSidebar = "open";
        this.selectedEventId = undefined;
        this.selectedAggregateEvent = aggregate;
        this.editingAnnotation = undefined;
    }

    updateRange(start: Date, end: Date) {
        const [s, e] = this.range;
        if (start.getTime() !== s.getTime() || end.getTime() !== e.getTime()) {
            this.range = [start, end];
        }
    }

    // Update the seek option so that it matches the selected event

    clampToRetentionPeriod = (date: Date): Date => {
        const { retentionPeriodStart } = this.organizationStore;

        return isBefore(date, retentionPeriodStart)
            ? retentionPeriodStart
            : date;
    };

    // (otherwise the event may not be visible)
    private fixSeekOption(eventType?: EventType) {
        const { seekOption } = this;
        if (
            eventType &&
            seekOption !== "Data presence" &&
            !seekOptionToEventTypes(seekOption).includes(eventType)
        ) {
            this.seekOption = eventTypeToSeekOption(eventType);
        }
    }
}
